import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { map, tap, switchMap } from 'rxjs/operators';
import { BehaviorSubject, from, Observable, Subject } from 'rxjs';
import { Platform } from '@ionic/angular';
import { Environment } from '../environment';

import { StorageService } from './storage.service';

const TOKEN_KEY = 'custhelper-token';

@Injectable({
    providedIn: 'root'
})
export class AuthenticationService {
    // Init with null to filter out the first value in a guard!
    authState: BehaviorSubject<boolean|null> = new BehaviorSubject<boolean|null>(null);
    token = '';

    constructor(private http: HttpClient,
                private storageService: StorageService,
                private platform: Platform,
                private env: Environment) {
        this.platform.ready().then(() => {
            this.init();
        });
    }

    init() {
        this.storageService.get(TOKEN_KEY).then(token => {
            if(token) {
                this.token = token;
                this.authState.next(true);
            }
        });
    }


    isAuthenticated() {
        return this.authState.value;
    }

    async loadCredentials() {
        return this.storageService.get('custhelper_credentials');
    }

    async loadToken() {
        return await this.storageService.get(TOKEN_KEY);
    }

    login(credentials: { username: string; password: string, remember_me: boolean, platform: string }, platform: string): Observable<any> {
        credentials['platform'] = platform;
	return this.http.post(this.env.getUrl('/user/login'), credentials).pipe(
	    map((data: any) => data.token),
	    switchMap((token) => {
                if(credentials.remember_me) {
                    this.storageService.set('custhelper_credentials', JSON.stringify(credentials));
                } else {
                    this.storageService.remove('custhelper_credentials');
                }
                return from(this.storageService.set(TOKEN_KEY, token));
	    }),
	    tap((_) => {
		this.authState.next(true);
	    })
	);
    }

    updatePushToken(pushToken: string): Observable<any> {
        return this.http.post(this.env.getUrl('/user/push-token'), {'token': pushToken}, {headers: this.getHeaders()}).pipe(
            map((data: any) => data.data));
    }

    registerAccount(userObj: object): Observable<any> {
        return this.http.post(this.env.getUrl('/user/create'), userObj, {headers: this.getHeaders()}).pipe(
            map((data: any) => data)
        );
    }

    logout(): Promise<void> {
	this.authState.next(false);
	return this.storageService.remove(TOKEN_KEY);
    }

    resetPassword(phone: string): Observable<any> {
        let urlSearchParams = new URLSearchParams();
        urlSearchParams.append('cellphone', phone);

        return this.http.get(this.env.getBaseUrl('/user/reset-password?') + urlSearchParams.toString()).pipe(
            map((data: any) => data)
        );
    }

    confirmCode(code: string): Observable<any> {
        let urlSearchParams = new URLSearchParams();
        urlSearchParams.append('code', code);

        return this.http.get(this.env.getBaseUrl('/user/confirm-code?') + urlSearchParams.toString()).pipe(
            map((data: any) => data)
        );
    }

    setNewPassword(token: string, newPassword: string): Observable<any> {
        return this.http.post(this.env.getBaseUrl('/user/reset-password'), {'token': token, 'newPassword': newPassword}, {headers: this.getHeaders()}).pipe(
            map((data: any) => data)
        );
    }

    getHeaders() {
        let headers = new HttpHeaders({
            'Authorization': 'Bearer ' + this.token,
            'Content-Type': 'application/json',
            'Accept': 'application/json'});
        return headers;
    }
}
